import { Component } from '@theme/component';

/**
 * What we do (sections/what-we-do.liquid): drives one CSS variable, `--wwd-progress` (0 to 1),
 * from how far the section has scrolled through the viewport. Everything visual (the ribbon's
 * dash offset, each pillar's fade and parallax) is computed in CSS from that one number.
 *
 * Only runs at desktop widths and under `prefers-reduced-motion: no-preference`, and only while
 * the section is on screen. Position comes from getBoundingClientRect, which is correct whether
 * the document or `.page-wrapper` is the scroll container.
 *
 * @extends {Component<{ canvas: HTMLElement }>}
 */
class WhatWeDoComponent extends Component {
  requiredRefs = ['canvas'];

  #media = window.matchMedia('(min-width: 990px) and (prefers-reduced-motion: no-preference)');

  /** @type {IntersectionObserver | undefined} */
  #observer;

  #frame = 0;

  #visible = false;

  connectedCallback() {
    super.connectedCallback();
    this.#media.addEventListener('change', this.#setup);
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#media.removeEventListener('change', this.#setup);
    this.#teardown();
  }

  #setup = () => {
    this.#teardown();
    if (!this.#media.matches) {
      this.removeAttribute('data-animate');
      return;
    }

    this.setAttribute('data-animate', '');
    this.#update();
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      if (this.#visible) this.#tick();
    });
    this.#observer.observe(this);
    window.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
  };

  #teardown() {
    this.#observer?.disconnect();
    window.removeEventListener('scroll', this.#onScroll, { capture: true });
    window.removeEventListener('resize', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  #onScroll = () => {
    if (this.#visible) this.#tick();
  };

  #tick() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const rect = this.refs.canvas.getBoundingClientRect();
    const progress = (window.innerHeight * 0.7 - rect.top) / (rect.height * 0.85);
    this.style.setProperty('--wwd-progress', Math.max(0, Math.min(1, progress)).toFixed(4));
  }
}

if (!customElements.get('what-we-do-component')) {
  customElements.define('what-we-do-component', WhatWeDoComponent);
}
